import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography, Grid, Skeleton, Breadcrumbs, Link } from '@mui/material';
import ProductCard from '../../common/ProductCard';
import { invokeGetApi, apiList } from '../../../services/apiServices';
import windowsImg from '../../../assets/images/windows_11_pro.png';
import officeImg from '../../../assets/images/ms_office_2024_pro.png';
import serverImg from '../../../assets/images/windows_server_2025.png';

// Map URL category slug → database category_id
const CATEGORY_ID_MAP = {
  'windows-os': 1,
  'microsoft-office': 2,
  'windows-server': 3,
  'other-services': 4,
};

const CategoryPage = () => {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const categoryDetails = {
    'windows-os': {
      title: 'Windows Operating Systems',
      description: 'Get original, lifetime activation keys for Windows 10 Pro, Windows 11 Pro, and more. Direct Microsoft server activation.',
      image: windowsImg
    },
    'microsoft-office': {
      title: 'Microsoft Office Suites',
      description: 'Genuine licenses for Office Home & Business and Professional Plus. Buy once, use lifetime.',
      image: officeImg
    },
    'windows-server': {
      title: 'Windows Server Standard',
      description: 'Authorized product licenses for Windows Server Standard environments.',
      image: serverImg
    }
  };

  const details = categoryDetails[category] || {
    title: category ? category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Products',
    description: 'Explore our range of premium software licenses and digital services.',
    image: windowsImg
  };

  useEffect(() => {
    const fetchCategoryProducts = async () => {
      setLoading(true);
      try {
        const response = await invokeGetApi(apiList.getAllProducts);
        const allProducts = response?.data?.products || response?.data || [];
        const categoryId = CATEGORY_ID_MAP[category];

        const filtered = categoryId
          ? allProducts.filter(p => Number(p.category_id) === categoryId)
          : allProducts;

        setProducts(filtered);
      } catch (error) {
        console.error("Error fetching category products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryProducts();
  }, [category]);

  return (
    <Box>
      {/* Category Hero */}
      <Box
        sx={{
          height: '400px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
          mb: 6
        }}
      >
        <Box
          component="img"
          src={details.image}
          sx={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', opacity: 0.3 }}
        />
        <Container sx={{ position: 'relative', zIndex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h2" sx={{ color: '#FFF', fontWeight: 700 }}>{details.title}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, maxWidth: '600px', mt: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
            {details.description}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mb: 10 }}>
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Link underline="hover" color="inherit" href="/shop">Shop</Link>
          <Typography color="text.primary">{details.title}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {loading ? (
            [1, 2, 3, 4].map((n) => (
              <Grid item xs={12} sm={6} md={3} key={n}>
                <Skeleton variant="rectangular" height={250} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
              </Grid>
            ))
          ) : (
            products.map((product) => (
              <Grid item xs={12} sm={6} md={3} key={product.id}>
                <ProductCard product={product} />
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default CategoryPage;
